/**
 * Breedte van de pane: zuivere regels (UX-review 17 oktober 2026, besluit Asil: eerst in ToD).
 *
 * Excel opent een pane op 320 px (Windows), 270 px (Mac) of 350 px (web) en auditors slepen zelden. Sinds de
 * requirement set TaskPaneApi 1.1 kan de add-in de breedte zelf zetten (`Office.extensionLifeCycle.taskpane.setWidth`).
 * De regels:
 *  - bij openen de voorkeursbreedte van de pane, of de breedte die de gebruiker eerder zelf koos (per pane
 *    onthouden). Zonder eigen keuze maken we de pane nooit smaller dan hij al is;
 *  - tijdelijk breder als de inhoud erom vraagt (bewijsviewer), daarna terug;
 *  - sleept de gebruiker zelf, dan onthouden we die breedte (voor de gewone stand of de brede stand).
 * Of setWidth CSS-pixels of schermpixels bedoelt, is per platform niet gedocumenteerd: `classifyResize` herkent
 * een resultaat dat precies een factor devicePixelRatio te smal is, zodat de hook één keer kan bijsturen.
 * Zonder React en Office, zodat de engine-tests ze draaien (tools/engine-tests/run-tests.js).
 */

/** Wat de pane per gebruiker onthoudt (localStorage); `wide` = de brede stand (viewer open). */
export interface PaneWidthMemory {
  base?: number;
  wide?: number;
}

/** Breedtes binnen deze marge gelden als gelijk (afronding, schuifbalk). */
export const WIDTH_TOLERANCE = 6;
/** Kleiner of groter dan dit onthouden we niet (onzin of een half scherm per ongeluk). */
export const MIN_REMEMBERED = 240;
export const MAX_REMEMBERED = 1600;

const near = (a: number, b: number, tol = WIDTH_TOLERANCE): boolean => Math.abs(a - b) <= tol;

/** Breedte die we bij openen vragen, of null als er niets te doen is. */
export function openTarget(
  preferred: number,
  memory: PaneWidthMemory,
  current: number
): number | null {
  const own = rememberable(memory.base);
  const want = own ?? preferred;
  if (near(current, want)) return null;
  // Zonder eigen keuze nooit smaller maken dan de pane al is (Excel of de gebruiker gaf hem die breedte).
  if (own === undefined && current > want) return null;
  return want;
}

/** Breedte voor de brede stand (viewer open), of null als de pane al breed genoeg is. */
export function widenTarget(wide: number, memory: PaneWidthMemory, current: number): number | null {
  const want = rememberable(memory.wide) ?? wide;
  return current >= want - WIDTH_TOLERANCE ? null : want;
}

/**
 * Breedte om naar terug te gaan na de brede stand: de eigen keuze, anders de breedte van vóór het verbreden.
 * Null als de pane al die breedte heeft of al smaller is (dan heeft de gebruiker zelf iets gedaan).
 */
export function restoreTarget(
  before: number,
  memory: PaneWidthMemory,
  current: number
): number | null {
  const want = rememberable(memory.base) ?? before;
  return current <= want + WIDTH_TOLERANCE ? null : want;
}

export interface ExpectedResize {
  /** Gevraagde breedte in CSS-pixels. */
  css: number;
  /** Breedte vóór de vraag. */
  from: number;
  /** Tijdstip van de vraag (ms). */
  at: number;
}

/** Hoe lang na een setWidth een resize nog als gevolg ervan geldt. */
export const OWN_RESIZE_WINDOW_MS = 2500;

/**
 * Een waargenomen breedte na een resize:
 *  - "own": gevolg van onze eigen setWidth (ook als Excel hem begrensde, bijvoorbeeld op de helft van het venster);
 *  - "scaled": precies een factor dpr te smal, setWidth rekende dus in schermpixels; één keer bijsturen;
 *  - "user": de gebruiker sleepte (of iets anders veranderde de breedte).
 */
export function classifyResize(
  observed: number,
  expected: ExpectedResize | null,
  now: number,
  dpr: number,
  scale: number
): "own" | "scaled" | "user" {
  if (!expected || now - expected.at > OWN_RESIZE_WINDOW_MS) return "user";
  if (near(observed, expected.css)) return "own";
  if (scale === 1 && dpr > 1.01 && near(observed * dpr, expected.css, WIDTH_TOLERANCE * dpr))
    return "scaled";
  // Begrensd door Excel: de breedte ging de goede kant op maar haalde het doel niet.
  const dir = Math.sign(expected.css - expected.from);
  const moved = observed - expected.from;
  if (
    dir !== 0 &&
    Math.sign(moved) === dir &&
    Math.abs(moved) < Math.abs(expected.css - expected.from)
  )
    return "own";
  return "user";
}

/** Alleen redelijke breedtes onthouden of gebruiken. */
export function rememberable(w: number | undefined): number | undefined {
  return typeof w === "number" && isFinite(w) && w >= MIN_REMEMBERED && w <= MAX_REMEMBERED
    ? Math.round(w)
    : undefined;
}
